import { useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import {
  recordRoom,
  type RoomRecordOutcome,
} from "@/adapters/supabase/roomRecord";
import type { RoomMode } from "@/engine/state/roomControl";
import { forgetOwnedRooms } from "@/engine/state/localOwnership";
import { reportAccountSignup } from "@/engine/diagnostics/signupConversion";
import {
  identifyAuthenticatedUser,
  resetAnalyticsIdentity,
} from "@/engine/diagnostics/analytics";

/**
 * Who the visitor is, when they are anybody.
 *
 * An account is not required for anything in the room: joining is anonymous,
 * the campfire label is still the name typed on the entry screen, and losing
 * the session simply returns the app to the behaviour it has always had. So
 * this is a plain observer of the session and never a gate.
 */

export interface AuthSession {
  session: Session | null;
  /** False only until the first answer arrives, so nothing flashes "signed out". */
  ready: boolean;
  email: string | null;
  signOut: () => Promise<void>;
}

export function useAuthSession(): AuthSession {
  const [session, setSession] = useState<Session | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let alive = true;
    let accountId: string | null = null;
    const applySession = (next: Session | null) => {
      setSession(next);
      setReady(true);
      if (next?.user) {
        // The Supabase UUID is stable; email remains only a person property.
        identifyAuthenticatedUser({
          id: next.user.id,
          email: next.user.email,
        });
        accountId = next.user.id;
        // Beside the session, never in front of it: the report self-censors on
        // consent and only fires for an account created moments ago.
        void reportAccountSignup({
          id: next.user.id,
          email: next.user.email,
          createdAt: next.user.created_at,
        });
      } else if (accountId) {
        resetAnalyticsIdentity();
        accountId = null;
      }
    };
    // Subscribe before reading, so a sign-in that lands between the two is
    // not missed.
    const { data } = supabase.auth.onAuthStateChange((_event, next) => {
      if (!alive) return;
      applySession(next);
    });
    void supabase.auth
      .getSession()
      .then(({ data: current }) => {
        if (!alive) return;
        applySession(current.session);
      })
      .catch(() => {
        if (alive) setReady(true);
      });
    return () => {
      alive = false;
      data.subscription.unsubscribe();
    };
  }, []);

  return {
    session,
    ready,
    email: session?.user.email ?? null,
    signOut: async () => {
      // A room created under the account stops being this tab's the moment the
      // account is gone; the local shortcut must go with it.
      forgetOwnedRooms();
      await supabase.auth.signOut();
    },
  };
}

/**
 * Notes down a room that was just created, under the creator's account when
 * there is one and as the kind of room they opened it as. Resolves either way
 * and never throws: nothing about entering the room waits for this. What it
 * resolves with is the one thing the host may need to hear — a row the database
 * refused leaves them holding a code that admits nobody.
 *
 * The kind is passed in rather than read from the preference store here. The
 * store may hydrate or change in another tab while the room is being opened,
 * and the room owns the exact choice made when Create was pressed — the same
 * reason `RoomSpace` captures it there.
 */
export async function noteCreatedRoom(
  code: string,
  roomMode: RoomMode,
  /**
   * The account the press already waited for. Looking it up again here is a
   * second read of a session that may still be settling, and a row written
   * with no owner is a room its creator does not host (#610).
   */
  ownerId?: string | null,
  /** What the host called the room, if anything. */
  displayName?: string,
): Promise<RoomRecordOutcome> {
  return recordRoom({
    code,
    ownerId: ownerId ?? (await currentAccountId()),
    roomMode,
    displayName,
  });
}

/**
 * The signed-in account, waited for.
 *
 * A visitor coming back from the identity provider lands on a page whose
 * session is still being restored, and asking once answers "nobody" to somebody
 * who has just signed in. Polling briefly is the difference between landing in
 * the room and being told to sign in again.
 */
export async function waitForAccountId(
  timeoutMs = 8000,
  step = 60,
): Promise<string | null> {
  const immediate = await currentAccountId();
  if (immediate) return immediate;
  // The restore usually lands as an auth event rather than at a poll tick, so
  // the wait listens for it and only polls as a fallback. Waiting a whole poll
  // interval for news that has already arrived is the delay this removes.
  return new Promise<string | null>((resolve) => {
    let settled = false;
    const finish = (id: string | null) => {
      if (settled) return;
      settled = true;
      window.clearTimeout(deadline);
      window.clearInterval(ticker);
      subscription.unsubscribe();
      resolve(id);
    };
    const { data } = supabase.auth.onAuthStateChange((_event, session) => {
      const id = session?.user.id ?? null;
      if (id) finish(id);
    });
    const subscription = data.subscription;
    const ticker = window.setInterval(() => {
      void currentAccountId().then((id) => {
        if (id) finish(id);
      });
    }, step);
    const deadline = window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * The signed-in account, or null. Read from the session rather than from React
 * state so a caller outside a component — the entry flow — can ask too.
 */
export async function currentAccountId(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.id ?? null;
  } catch {
    return null;
  }
}

/**
 * The signed-in account's email address, or null. Read the same way as the
 * account id, for callers outside React — the ad conversion path asks for it
 * so Google can match a conversion to the click that earned it.
 */
export async function currentAccountEmail(): Promise<string | null> {
  try {
    const { data } = await supabase.auth.getSession();
    return data.session?.user.email ?? null;
  } catch {
    return null;
  }
}
